import { useEffect } from 'react';
import { configApiRef, discoveryApiRef, microsoftAuthApiRef, useApi, type SignInPageProps } from '@backstage/core-plugin-api';
import { UserIdentity } from '@backstage/core-components';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { SignInPageBlueprint } from '@backstage/plugin-app-react';
import { AutoProviderSignInPage } from '../components/sign-in/AutoProviderSignInPage';

const microsoftAuthProvider = {
  id: 'azure-auth-provider',
  title: 'Microsoft Active Directory',
  message:
    'Sign in to Backstage Application using your Active Directory account.',
  apiRef: microsoftAuthApiRef,
};

/**
 * E2E sign-in: exchanges the guest provider's refresh endpoint for a real
 * backstage token so backend calls (catalog, permission, etc.) succeed
 * instead of returning 401. Falls back to a static guest identity if the
 * backend guest provider is unavailable.
 */
function E2eBypassSignInPage(props: SignInPageProps) {
  const discoveryApi = useApi(discoveryApiRef);
  const { onSignInSuccess } = props;

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const authBase = await discoveryApi.getBaseUrl('auth');
        const res = await fetch(`${authBase}/guest/refresh`, {
          method: 'POST',
          credentials: 'include',
        });
        if (!res.ok) throw new Error(`guest refresh failed: ${res.status}`);
        const data = await res.json();
        if (cancelled) return;

        const token: string = data?.backstageIdentity?.token;
        const identity = data?.backstageIdentity?.identity;

        onSignInSuccess(
          UserIdentity.fromLegacy({
            userId: identity?.userEntityRef ?? 'user:default/guest',
            profile: data?.profile ?? {
              email: 'guest@example.com',
              displayName: 'Guest',
            },
            getIdToken: token ? async () => token : undefined,
          }),
        );
      } catch {
        if (!cancelled) {
          onSignInSuccess(UserIdentity.createGuest());
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [discoveryApi, onSignInSuccess]);

  return null;
}

function ConfiguredSignInPage(props: SignInPageProps) {
  const configApi = useApi(configApiRef);
  const authEnvironment = configApi.getOptionalString('auth.environment');
  const e2eAuthBypass = configApi.getOptionalBoolean('app.e2e.authBypass') === true;
  const e2eBypassFromQuery =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('e2e-auth-bypass') === 'true';

  if ((e2eAuthBypass || e2eBypassFromQuery) && authEnvironment === 'local') {
    return <E2eBypassSignInPage {...props} />;
  }

  return <AutoProviderSignInPage {...props} provider={microsoftAuthProvider} />;
}

const signInPage = SignInPageBlueprint.make({
  params: {
    loader: async () => {
      return (props: SignInPageProps) => <ConfiguredSignInPage {...props} />;
    },
  },
});

export const signInModule = createFrontendModule({
  pluginId: 'app',
  extensions: [signInPage],
});
